/* eslint-disable @typescript-eslint/non-nullable-type-assertion-style */
import React, { useState, useEffect } from 'react';
import {
  Button,
  Spin,
  Input,
  Form,
  type DatePickerProps,
  DatePicker,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
// import type { DatePickerProps } from 'antd';
// import dayjs, { type Dayjs } from 'dayjs';
import {
  type DepositPayment,
  type DepositResponse,
} from '../store/depositTypes';
import { depositPaymentAction } from '../store/depositActions';
import dayjs, { type Dayjs } from 'dayjs';
// import { depositPaymentAction } from '../store/depositActions';

// const { TextArea } = Input;

interface Props {
  depositDetail: DepositResponse;
  closeModalNOOP: () => void;
}

export default function DepositPaymentModal({
  depositDetail,
  closeModalNOOP,
}: Props): JSX.Element {
  const [datePickerDate, setDatePickerDate] = useState<Dayjs | null>(dayjs());
  const [calculatedInterest, setCalculatedInterest] = useState<number>(
    depositDetail.pendingInterest ?? Number(depositDetail.interest)
  );
  const [calculatedDays, setCalculatedDays] = useState<number>(
    depositDetail.interestDays ?? 0
  );
  const [onlineInterest, setOnlineInterest] = useState<number>(0);
  const [offlineInterest, setOfflineInterest] = useState<number>(0);
  const [reducingInterest, setReducingInterest] = useState<number>(0);
  const [isValid, setIsValid] = useState<boolean>(false);

  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({
      // InvestedId: Number(depositDetail.id),
      transactionDate: dayjs().format('YYYY-MM-DD'),
      interestAmount: depositDetail.pendingInterest ?? depositDetail.interest,
    });
  }, []);

  const calculateInterest = (selectedDate: Dayjs | null): void => {
    if (selectedDate === null) return;

    // Determine start date
    let startDate: Date;
    if (
      depositDetail.lastPaymentDate != null &&
      depositDetail.lastPaymentDate !== ''
    ) {
      startDate = new Date(depositDetail.lastPaymentDate);
      startDate.setDate(startDate.getDate() + 1);
    } else {
      startDate = new Date(depositDetail.investedDate);
    }

    // Set to start of day (00:00:00)
    startDate.setHours(0, 0, 0, 0);

    // Get selected date and set to end of day (23:59:59)
    const endDate = selectedDate.toDate();
    endDate.setHours(23, 59, 59, 999);

    // Calculate days difference
    const millisDiff = endDate.getTime() - startDate.getTime();
    const daysDifference =
      millisDiff <= 0 ? 0 : Math.ceil(millisDiff / (1000 * 60 * 60 * 24));

    // Calculate interest
    const perDayInterest = depositDetail.perDayInterest ?? 0;
    const newInterest = Math.round(perDayInterest * daysDifference);

    setCalculatedDays(daysDifference);
    setCalculatedInterest(newInterest);
    form.setFieldsValue({ interestAmount: newInterest });
  };

  const onChangeDate: DatePickerProps['onChange'] = (
    date: Dayjs | null,
    dateString: string
  ): void => {
    setDatePickerDate(date);
    console.log('dateString', dateString);
    form.setFieldsValue({ transactionDate: dateString });
    calculateInterest(date);
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const dispatch = useDispatch<AppDispatch>();

  // Validate that reducing + online + offline = pending interest
  useEffect(() => {
    const total = reducingInterest + onlineInterest + offlineInterest;
    setIsValid(total === calculatedInterest);
  }, [reducingInterest, onlineInterest, offlineInterest, calculatedInterest]);

  const onFinish = async (values: DepositPayment) => {
    if (!isValid) {
      return;
    }

    setIsLoading(true);
    const action = depositPaymentAction;
    const branchId =
      typeof depositDetail.branch === 'string'
        ? depositDetail.branch
        : depositDetail.branch._id;
    const response = await action(dispatch, {
      ...values,
      depositNumber: depositDetail.depositNumber,
      branch: branchId,
      interestAmount:
        Number(form.getFieldValue('interestAmount')) -
        Number(form.getFieldValue('reducingInterest') ?? 0),
      reducingInterest: Number(form.getFieldValue('reducingInterest')),
      onlineInterest: Number(form.getFieldValue('onlineInterest')),
      offlineInterest: Number(form.getFieldValue('offlineInterest')),
      transactionDate: form.getFieldValue('transactionDate'),
      interestDays: calculatedDays,
    });
    setIsLoading(false);
    if (response !== null) {
      form.resetFields();
      closeModalNOOP();
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <div className="deposit__payment__modal">
      <div className="divider" />
      <Form
        form={form}
        name="payment_form"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
      >
        <Spin spinning={isLoading} fullscreen />
        <div className="payment__form">
          {/* <Form.Item name="InvestedId"> */}
          <div className="payment__input__container">
            <p className="payment__input__label">Invested By</p>
            <Input
              value={depositDetail?.customerName}
              disabled
              className="payment__input"
              placeholder="Enter Customer Name"
            />
          </div>
          {/* </Form.Item> */}
          <Form.Item
            name="interestAmount"
            rules={[
              { required: true, message: 'Please enter interest amount' },
            ]}
          >
            <div className="payment__input__container">
              <p className="payment__input__label">
                Pending Interest ({calculatedDays} days)
              </p>
              <Input
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                disabled
                value={calculatedInterest}
                className="payment__input"
                placeholder="Enter pending interest"
              />
            </div>
          </Form.Item>
          <Form.Item
            name="onlineInterest"
            rules={[
              { required: true, message: 'Please enter online interest' },
            ]}
          >
            <div className="payment__input__container">
              <p className="payment__input__label">Online Interest</p>
              <Input
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setOnlineInterest(isNaN(value) ? 0 : value);
                }}
                className="payment__input"
                placeholder="Enter online interest"
              />
            </div>
          </Form.Item>
          <Form.Item
            name="offlineInterest"
            rules={[
              { required: true, message: 'Please enter offline interest' },
            ]}
          >
            <div className="payment__input__container">
              <p className="payment__input__label">Offline Interest</p>
              <Input
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setOfflineInterest(isNaN(value) ? 0 : value);
                }}
                className="payment__input"
                placeholder="Enter offline interest"
              />
            </div>
          </Form.Item>
          <Form.Item name="reducingInterest">
            <div className="payment__input__container">
              <p className="payment__input__label">Reducing Interest</p>
              <Input
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setReducingInterest(isNaN(value) ? 0 : value);
                }}
                className="payment__input"
                placeholder="Enter reducing interest"
              />
            </div>
          </Form.Item>
          {!isValid && (onlineInterest > 0 || offlineInterest > 0) && (
            <div
              style={{ color: 'red', marginBottom: 16, textAlign: 'center' }}
            >
              Online + Offline must equal Pending Interest ({calculatedInterest}
              )
            </div>
          )}
          {/* <Form.Item
            name="OfflineAmount"
            rules={[{ required: true, message: 'Please enter offline amount' }]}
          >
            <div className="payment__input__container">
              <p className="payment__input__label">Offline amount</p>
              <Input
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                disabled
                value={depositDetail.offlineAmount}
                className="payment__input"
                placeholder="Enter offline amount"
              />
            </div>
          </Form.Item> */}
          {/* <Form.Item
            name="CollectedBy"
            rules={[{ required: true, message: 'Please enter collected by' }]}
          >
            <div className="payment__input__container">
              <p className="payment__input__label">Collected By</p>
              <Input
                className="payment__input"
                placeholder="Enter collected by"
              />
            </div>
          </Form.Item> */}
          <Form.Item
            name="transactionDate"
            rules={[
              { required: true, message: 'Please select transaction date!' },
            ]}
          >
            <div className="payment__input__container">
              <p className="payment__input__label">Transaction Date</p>
              <DatePicker
                value={datePickerDate}
                className="payment__input"
                onChange={onChangeDate}
              />
            </div>
          </Form.Item>
        </div>
        <div className="payment__actions__section">
          <Button
            onClick={() => {
              closeModalNOOP();
            }}
            className="payment__cancel__button"
          >
            Cancel
          </Button>
          <Button
            htmlType="submit"
            className="payment__save__button"
            disabled={!isValid}
          >
            {isLoading ? (
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 24, color: 'white' }}
                    spin
                  />
                }
              />
            ) : (
              <span>Pay</span>
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
}
